import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getCalendarBlocks,
  manageCalendarBlock,
  selectCalendarBlocks,
} from "../../store/slices/bookingSlice";
import api from "../../utils/axios";
import {
  Calendar, ChevronLeft, ChevronRight, Lock, Unlock, Loader2,
  Plus, User, Phone, Users, CheckCircle2, XCircle, X, Clock, Wrench,
} from "lucide-react";
import toast from "react-hot-toast";
import { ListSkeleton } from "../UI/Skeleton";

const SLOTS = ["morning", "afternoon", "evening", "full-day"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const FUNCTION_TYPES = [
  "wedding", "birthday", "anniversary", "corporate_event", "conference",
  "reception", "engagement", "baby_shower", "kitty_party", "social_gathering", "other",
];

// What can hold a slot: colour of its dot + the label shown for it
const REASON_META = {
  booking_confirmed: { label: "Booked",       dot: "bg-red-500" },
  manual_block:      { label: "Manual Block", dot: "bg-orange-400" },
  maintenance:       { label: "Maintenance",  dot: "bg-yellow-400" },
};

const statusBadge = {
  pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
  confirmed: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  auto_rejected: "bg-red-50 text-red-700 border-red-200",
  cancelled_by_user: "bg-gray-50 text-gray-600 border-gray-200",
  cancelled_by_vendor: "bg-orange-50 text-orange-700 border-orange-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const pad = (n) => String(n).padStart(2, "0");
const formatAmount = (a) => `₹${new Intl.NumberFormat("en-IN").format(a || 0)}`;

// Blocks are stored at IST midnight, which is the previous day at 18:30 in UTC.
// Key by the IST calendar date (+5:30 offset) so it lines up with the calendar
// cells — otherwise a block on the 15th shows on the 14th.
const istDateKey = (date) =>
  new Date(new Date(date).getTime() + 5.5 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

const EMPTY_FORM = {
  name: "",
  phone: "",
  slot: "full-day",
  number_of_guests: "",
  function_type: "wedding",
  total_amount: "",
  special_requests: "",
};

export default function CalendarBlockManager({ venues }) {
  const dispatch = useDispatch();
  const { data: blocks, loading } = useSelector(selectCalendarBlocks);

  const [venueId, setVenueId] = useState(venues?.[0]?._id || "");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState("full-day");
  const [blockReason, setBlockReason] = useState("manual_block"); // manual_block | maintenance

  // Online + manual bookings for the selected day
  const [dayBookings, setDayBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [actionId, setActionId] = useState(null);

  // Manual booking form
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const dateStr =
    selectedDate != null
      ? `${year}-${pad(month)}-${pad(selectedDate)}`
      : null;

  const fetchBlocks = useCallback(() => {
    if (venueId) dispatch(getCalendarBlocks({ venue_id: venueId, year, month }));
  }, [dispatch, venueId, year, month]);

  useEffect(fetchBlocks, [fetchBlocks]);

  const fetchDayBookings = useCallback(async () => {
    if (!venueId || !dateStr) {
      setDayBookings([]);
      return;
    }
    try {
      setBookingsLoading(true);
      const { data } = await api.get("/api/vendor/bookings", {
        params: { service_id: venueId, date: dateStr, limit: 50 },
      });
      if (data.success) setDayBookings(data.data || []);
    } catch {
      toast.error("Failed to load bookings for this date");
    } finally {
      setBookingsLoading(false);
    }
  }, [venueId, dateStr]);

  useEffect(() => { fetchDayBookings(); }, [fetchDayBookings]);

  const blockMap = {};
  (blocks || []).forEach((b) => {
    const key = istDateKey(b.date);
    if (!blockMap[key]) blockMap[key] = [];
    blockMap[key].push(b);
  });

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfWeek = (new Date(year, month - 1, 1).getDay() + 6) % 7; // Monday=0

  const navigateMonth = (dir) => {
    let m = month + dir;
    let y = year;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMonth(m);
    setYear(y);
    setSelectedDate(null);
    setShowForm(false);
  };

  const refreshAll = () => { fetchBlocks(); fetchDayBookings(); };

  // A slot counts as "blocked" if a vendor block (manual or maintenance) exists.
  const vendorBlockOn = (slot) =>
    (blockMap[dateStr] || []).find(
      (b) => b.slot === slot && ["manual_block", "maintenance"].includes(b.reason)
    );

  const handleToggleBlock = async () => {
    if (!selectedDate || !venueId) return;
    const existing = vendorBlockOn(selectedSlot);
    try {
      await dispatch(
        manageCalendarBlock({
          venue_id: venueId,
          date: dateStr,
          slot: selectedSlot,
          action: existing ? "unblock" : "block",
          reason: blockReason,
        })
      ).unwrap();
      toast.success(
        existing
          ? "Slot unblocked"
          : blockReason === "maintenance"
            ? "Marked for maintenance"
            : "Slot blocked"
      );
      refreshAll();
    } catch (err) {
      toast.error(err || "Failed");
    }
  };

  const handleConfirm = async (id) => {
    try {
      setActionId(id);
      await api.put(`/api/vendor/bookings/${id}/confirm`, {
        response_message: "Confirmed by vendor",
      });
      toast.success("Booking confirmed");
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to confirm");
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Reason for rejection:");
    if (!reason) return;
    try {
      setActionId(id);
      await api.put(`/api/vendor/bookings/${id}/reject`, { rejection_reason: reason });
      toast.success("Booking rejected");
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reject");
    } finally {
      setActionId(null);
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openForm = () => {
    setForm({ ...EMPTY_FORM, slot: selectedSlot });
    setShowForm(true);
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!venueId || !dateStr) return;
    if (!form.name.trim()) return toast.error("Customer name is required");
    if (!/^\d{10}$/.test(form.phone.trim()))
      return toast.error("Enter a valid 10-digit phone number");
    if (!form.number_of_guests || Number(form.number_of_guests) < 1)
      return toast.error("Number of guests is required");

    try {
      setSaving(true);
      await api.post("/api/vendor/bookings/manual", {
        service_type: "venue",
        venue_id: venueId,
        event_date: dateStr,
        slot: form.slot,
        guest_details: { name: form.name.trim(), phone: form.phone.trim() },
        number_of_guests: Number(form.number_of_guests),
        function_type: form.function_type,
        special_requests: form.special_requests.trim() || undefined,
        total_amount: form.total_amount !== "" ? Number(form.total_amount) : undefined,
      });
      toast.success("Manual booking recorded");
      setShowForm(false);
      setForm(EMPTY_FORM);
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to record booking");
    } finally {
      setSaving(false);
    }
  };

  const monthName = new Date(year, month - 1).toLocaleString("en-IN", { month: "long" });

  const now = new Date();
  const isCurrentMonth = now.getFullYear() === year && now.getMonth() + 1 === month;
  const goToToday = () => {
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
    setSelectedDate(now.getDate());
    setShowForm(false);
  };

  // What the selected day's slot is currently held by, if anything
  const slotState = (slot) => {
    const block = (blockMap[dateStr] || []).find((b) => b.slot === slot);
    return block ? REASON_META[block.reason] || { label: "Blocked", dot: "bg-gray-400" } : null;
  };

  const selectedWeekday = selectedDate
    ? new Date(year, month - 1, selectedDate).toLocaleDateString("en-IN", { weekday: "long" })
    : "";

  const INPUT = "w-full h-10 px-3.5 text-sm text-gray-900 placeholder:text-gray-400 bg-white border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all";
  const LABEL = "block text-[13px] font-semibold text-gray-700 mb-1.5";
  const CARD = "bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)]";

  if (!venues || venues.length === 0) {
    return (
      <div className="bg-white border border-dashed border-gray-300 rounded-2xl px-6 py-14 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-50">
          <Calendar className="w-6 h-6 text-purple-500" />
        </div>
        <h3 className="font-['Manrope'] text-lg font-bold text-gray-900 mb-1">No venues yet</h3>
        <p className="text-sm text-gray-500">
          Add a venue from the Services tab to manage its calendar and bookings.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Toolbar: title + venue ─────────────────────────────── */}
      <div className={`${CARD} p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-4`}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20">
            <Calendar size={18} className="text-white" />
          </span>
          <div className="min-w-0">
            <h3 className="font-['Manrope'] text-base font-bold text-gray-900 leading-tight">Calendar Manager</h3>
            <p className="text-xs text-gray-500 mt-0.5">Block dates, mark maintenance and record walk-in bookings.</p>
          </div>
        </div>
        <label className="flex items-center gap-2.5 md:w-80">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 whitespace-nowrap">Venue</span>
          <select
            value={venueId}
            onChange={(e) => { setVenueId(e.target.value); setSelectedDate(null); setShowForm(false); }}
            className={`${INPUT} font-semibold`}
          >
            {(venues || []).map((v) => (
              <option key={v._id} value={v._id}>{v.name}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] gap-5 items-start">
        {/* ── Month grid ───────────────────────────────────────── */}
        <div className={`${CARD} p-4 sm:p-6`}>
          <div className="flex items-center justify-between gap-3 mb-5">
            <h4 className="font-['Manrope'] text-xl font-extrabold tracking-tight text-gray-900">
              {monthName} <span className="text-gray-400">{year}</span>
            </h4>
            <div className="flex items-center gap-1.5">
              <button
                onClick={goToToday}
                className="h-9 px-3.5 rounded-xl border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:border-purple-200 hover:bg-purple-50 transition-colors"
              >
                Today
              </button>
              <button
                onClick={() => navigateMonth(-1)}
                aria-label="Previous month"
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 hover:border-purple-200 hover:bg-purple-50 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => navigateMonth(1)}
                aria-label="Next month"
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 hover:border-purple-200 hover:bg-purple-50 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
            {DAYS.map((d) => (
              <div key={d} className="pb-1 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-400">{d}</div>
            ))}

            {Array.from({ length: firstDayOfWeek }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const key = `${year}-${pad(month)}-${pad(day)}`;
              const dayBlocks = blockMap[key] || [];
              const isSelected = selectedDate === day;
              const isToday = isCurrentMonth && now.getDate() === day;
              const isPast = new Date(year, month - 1, day) < new Date(new Date().setHours(0, 0, 0, 0));

              return (
                <button
                  key={day}
                  onClick={() => { if (!isPast) { setSelectedDate(isSelected ? null : day); setShowForm(false); } }}
                  disabled={isPast}
                  aria-pressed={isSelected}
                  className={`relative flex h-14 sm:h-[4.5rem] flex-col items-center justify-center gap-1 rounded-xl border text-sm font-semibold tabular-nums transition-all ${
                    isPast
                      ? "border-transparent bg-gray-50/60 text-gray-300 cursor-not-allowed"
                      : isSelected
                        ? "border-transparent bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-md shadow-purple-600/25"
                        : isToday
                          ? "border-purple-300 bg-purple-50/60 text-gray-900 hover:bg-purple-50"
                          : "border-gray-200/70 bg-white text-gray-800 hover:border-purple-200 hover:bg-purple-50/40"
                  }`}
                >
                  {day}
                  {isToday && !isSelected && (
                    <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-purple-600" />
                  )}
                  <span className="flex h-1.5 gap-0.5">
                    {dayBlocks.slice(0, 4).map((b, idx) => (
                      <span
                        key={idx}
                        className={`h-1.5 w-1.5 rounded-full ${isSelected ? "ring-1 ring-white/70" : ""} ${REASON_META[b.reason]?.dot || "bg-gray-400"}`}
                      />
                    ))}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-gray-100">
            {Object.values(REASON_META).map((meta) => (
              <span key={meta.label} className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600">
                <span className={`h-2 w-2 rounded-full ${meta.dot}`} /> {meta.label}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600">
              <span className="h-2 w-2 rounded-full bg-purple-600" /> Today
            </span>
          </div>
        </div>

        {/* ── Selected day ─────────────────────────────────────── */}
        <div className="space-y-5">
          {!selectedDate ? (
            <div className="bg-white border border-dashed border-gray-300 rounded-2xl px-6 py-14 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-50">
                <Clock className="w-6 h-6 text-purple-500" />
              </div>
              <h4 className="font-['Manrope'] text-base font-bold text-gray-900">Pick a date</h4>
              <p className="text-sm text-gray-500 mt-1 max-w-xs mx-auto">
                Select a date on the calendar to view and manage its bookings, or to record a manual on-site booking.
              </p>
            </div>
          ) : (
            <>
              {/* Slots + actions */}
              <div className={`${CARD} p-4 sm:p-6`}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex h-12 w-12 flex-shrink-0 flex-col items-center justify-center rounded-xl border border-purple-100 bg-purple-50/70 leading-none">
                    <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500">{monthName.slice(0, 3)}</span>
                    <span className="mt-0.5 font-['Manrope'] text-lg font-extrabold text-gray-900 tabular-nums">{selectedDate}</span>
                  </div>
                  <div className="min-w-0">
                    <h4 className="font-['Manrope'] text-base font-bold text-gray-900">{selectedWeekday}, {selectedDate} {monthName}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">Choose a slot, then block it or record a booking.</p>
                  </div>
                </div>

                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Slot</p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {SLOTS.map((slot) => {
                    const state = slotState(slot);
                    const isActive = selectedSlot === slot;
                    return (
                      <button
                        key={slot}
                        onClick={() => setSelectedSlot(slot)}
                        aria-pressed={isActive}
                        className={`rounded-xl border p-3 text-left transition-all ${
                          isActive
                            ? "border-purple-400 bg-purple-50/70 ring-4 ring-purple-500/10"
                            : "border-gray-200/70 bg-white hover:border-purple-200"
                        }`}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-gray-900 capitalize">{slot}</span>
                          {state && <Lock className="h-3.5 w-3.5 text-gray-400" />}
                        </span>
                        <span className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                          <span className={`h-1.5 w-1.5 rounded-full ${state ? state.dot : "bg-green-500"}`} />
                          {state ? state.label : "Available"}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Block-type selector — only relevant when the slot isn't blocked yet */}
                {!vendorBlockOn(selectedSlot) && (
                  <>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2">Block as</p>
                    <div className="inline-flex gap-1 rounded-xl border border-gray-200 p-1 mb-4">
                      {[
                        { key: "manual_block", label: "Manual Block" },
                        { key: "maintenance", label: "Maintenance" },
                      ].map((t) => (
                        <button
                          key={t.key}
                          onClick={() => setBlockReason(t.key)}
                          aria-pressed={blockReason === t.key}
                          className={`flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-semibold transition-colors ${
                            blockReason === t.key ? "bg-purple-50 text-gray-900" : "text-gray-500 hover:text-gray-900"
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${REASON_META[t.key].dot}`} />
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <button
                    onClick={handleToggleBlock}
                    disabled={loading}
                    className="h-11 px-4 rounded-xl border border-gray-200 bg-white text-gray-800 text-sm font-semibold hover:border-purple-200 hover:bg-purple-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {loading ? <Loader2 size={14} className="animate-spin" /> :
                      vendorBlockOn(selectedSlot)
                        ? <><Unlock size={14} /> Unblock Slot</>
                        : blockReason === "maintenance"
                          ? <><Wrench size={14} /> Mark Maintenance</>
                          : <><Lock size={14} /> Block Slot</>
                    }
                  </button>
                  <button
                    onClick={openForm}
                    className="h-11 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold shadow-md shadow-purple-600/20 hover:shadow-lg transition-all flex items-center justify-center gap-2"
                  >
                    <Plus size={14} /> Manual Booking
                  </button>
                </div>
              </div>

              {/* Bookings on this date */}
              <div className={`${CARD} p-4 sm:p-6`}>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <h4 className="font-['Manrope'] text-base font-bold text-gray-900">Bookings on this date</h4>
                  <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-semibold text-gray-600 tabular-nums">
                    {dayBookings.length}
                  </span>
                </div>

                {bookingsLoading ? (
                  <ListSkeleton rows={3} />
                ) : dayBookings.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-300 px-4 py-9 text-center">
                    <p className="text-sm font-semibold text-gray-900">No bookings on this date yet.</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Use <span className="font-semibold text-gray-900">Manual Booking</span> to record a walk-in.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {dayBookings.map((b) => (
                      <div key={b._id} className="rounded-xl border border-gray-200/70 bg-white p-4 hover:border-purple-200 hover:shadow-md hover:shadow-purple-900/5 transition-all">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-gray-900 text-sm">
                                {b.guest_details?.name || b.user_id?.full_name || "Guest"}
                              </span>
                              <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full border capitalize ${statusBadge[b.booking_status] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
                                {b.booking_status?.replace(/_/g, " ")}
                              </span>
                              {b.booking_source === "offline" && (
                                <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full border bg-white text-gray-600 border-gray-200">
                                  On-site
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 mt-1.5 flex items-center gap-3 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Phone size={11} /> {b.contact_phone || b.user_id?.phone || "—"}
                              </span>
                              <span className="capitalize">{b.slot}</span>
                              {b.number_of_guests ? (
                                <span className="flex items-center gap-1"><Users size={11} /> {b.number_of_guests}</span>
                              ) : null}
                            </div>
                            <div className="mt-2 flex items-center gap-2 text-xs">
                              <span className="font-['Manrope'] text-sm font-bold text-gray-900 tabular-nums">{formatAmount(b.pricing_details?.total_amount)}</span>
                              <span className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-gray-600">#{b.booking_reference}</span>
                            </div>
                          </div>

                          {b.booking_status === "pending" && (
                            <div className="flex flex-col gap-1.5 flex-shrink-0">
                              <button
                                onClick={() => handleConfirm(b._id)}
                                disabled={actionId === b._id}
                                className="flex items-center justify-center gap-1 h-8 px-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-semibold shadow-md shadow-purple-600/20 hover:shadow-lg disabled:opacity-50 transition-all"
                              >
                                {actionId === b._id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                                Confirm
                              </button>
                              <button
                                onClick={() => handleReject(b._id)}
                                disabled={actionId === b._id}
                                className="flex items-center justify-center gap-1 h-8 px-3 rounded-lg bg-white border border-gray-200 text-gray-700 text-xs font-semibold hover:bg-red-50 hover:border-red-200 hover:text-red-600 disabled:opacity-50 transition-all"
                              >
                                <XCircle size={12} /> Reject
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Manual Booking modal ──────────────────────────────── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl shadow-purple-900/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
              <div className="flex items-center gap-3 min-w-0">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20"><Plus size={18} className="text-white" /></span>
                <div className="min-w-0">
                  <h3 className="font-['Manrope'] text-base font-bold text-gray-900">Record Manual Booking</h3>
                  <p className="text-xs text-gray-500 truncate">
                    {venues.find((v) => v._id === venueId)?.name} · {selectedDate} {monthName} {year}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowForm(false)}
                aria-label="Close"
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleManualSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Customer Name *</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      className={`${INPUT} pl-10`}
                      placeholder="Full name"
                    />
                  </div>
                </div>
                <div>
                  <label className={LABEL}>Phone *</label>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                      className={`${INPUT} pl-10`}
                      placeholder="10-digit number"
                      inputMode="numeric"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Slot *</label>
                  <select
                    value={form.slot}
                    onChange={(e) => setField("slot", e.target.value)}
                    className={`${INPUT} capitalize`}
                  >
                    {SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Number of Guests *</label>
                  <input
                    type="number"
                    min="1"
                    value={form.number_of_guests}
                    onChange={(e) => setField("number_of_guests", e.target.value)}
                    className={INPUT}
                    placeholder="e.g. 200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL}>Function Type</label>
                  <select
                    value={form.function_type}
                    onChange={(e) => setField("function_type", e.target.value)}
                    className={`${INPUT} capitalize`}
                  >
                    {FUNCTION_TYPES.map((f) => (
                      <option key={f} value={f}>{f.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL}>
                    Total Amount (₹) <span className="font-normal text-gray-400">— optional</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.total_amount}
                    onChange={(e) => setField("total_amount", e.target.value)}
                    className={INPUT}
                    placeholder="Auto from venue if blank"
                  />
                </div>
              </div>

              <div>
                <label className={LABEL}>Special Requests</label>
                <textarea
                  value={form.special_requests}
                  onChange={(e) => setField("special_requests", e.target.value)}
                  rows={2}
                  maxLength={500}
                  className="w-full px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 border border-gray-200 rounded-xl hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400 transition-all resize-none"
                  placeholder="Any notes about this booking"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="h-11 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="h-11 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold shadow-md shadow-purple-600/20 hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  Save Booking
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
